'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'

import { useRouter } from '@/src/i18n/navigation'
import useHolidayAppMode from '@/src/hooks/holidays/useHolidayAppMode'
import { withHolidayAppMode } from '@/src/utils/holidays'
import type { HolidayPackageFacet, HolidayPackageFilters } from '@/src/utils/holidays/packageFilters'
import {
    HOLIDAY_PACKAGES_PATH,
    buildMonthWindow,
    formatHolidayMonth,
    formatHolidayMonthParts,
    toggleFilterValue
} from '@/src/utils/holidays/packageFilters'

import PackagesSearchBar, { type MonthTile } from './packagesSearchBar'

interface PackagesSearchBarWrapperProps {
    cityFacets: HolidayPackageFacet[]
    monthFacets: HolidayPackageFacet[]
    /** The filters currently applied to the grid. */
    filters: HolidayPackageFilters
    /** Size of the unfiltered catalogue, for the "browse all N packages" line. */
    totalPackages: number
    onApply: (cities: string[], months: string[]) => void
    /**
     * `filter` — the bar edits the grid it sits above, so the button waits until the draft
     * differs. `search` — the bar sends the reader somewhere else, which is worth doing even
     * with nothing picked, so the button is always live.
     */
    applyMode?: 'filter' | 'search'
    applyLabel?: string
    isMobile: boolean
}

/** Destinations offered in the dropdown before the reader falls back to the side filter. */
const POPULAR_CITY_COUNT = 6

/** A rolling nine months of departures, which fills the picker's three-column grid exactly. */
const MONTH_WINDOW = 9

const sameValues = (a: string[], b: string[]) => a.length === b.length && a.every(value => b.includes(value))

const PackagesSearchBarWrapper: React.FC<PackagesSearchBarWrapperProps> = ({
    cityFacets,
    monthFacets,
    filters,
    totalPackages,
    onApply,
    applyMode = 'filter',
    applyLabel,
    isMobile
}) => {
    const t = useTranslations('holidaysPage')
    const router = useRouter()
    const locale = useLocale()
    const isApp = useHolidayAppMode()

    /*
     * The bar is a draft, not a filter: picking a destination or a month changes what the fields
     * read, and only "update results" reaches the grid. The side filters stay immediate — the
     * difference is that a search is composed, where ticking one box is a single decision.
     */
    const [pendingCities, setPendingCities] = useState(filters.cities)
    const [pendingMonths, setPendingMonths] = useState(filters.months)

    // The applied filters can change without the bar — a side filter, a chip, the Back button —
    // and the draft follows them, or the fields would keep showing an abandoned search.
    useEffect(() => {
        setPendingCities(filters.cities)
        setPendingMonths(filters.months)
    }, [filters.cities, filters.months])

    // Read once, in an initialiser rather than during render, so the window cannot change
    // between two renders of the same page.
    const [currentMonth] = useState(() => new Date().toISOString().slice(0, 7))

    const popularCities = useMemo(
        () =>
            cityFacets
                .slice(0, POPULAR_CITY_COUNT)
                .map(facet => ({ ...facet, selected: pendingCities.includes(facet.key) })),
        [cityFacets, pendingCities]
    )

    const monthTiles: MonthTile[] = useMemo(() => {
        const counts = new Map(monthFacets.map(facet => [facet.key, facet.count]))
        return buildMonthWindow(currentMonth, MONTH_WINDOW).map(key => {
            const count = counts.get(key) ?? 0
            const { month, year } = formatHolidayMonthParts(key, locale)
            return {
                key,
                label: `${month} ${year}`,
                monthLabel: month,
                yearLabel: year,
                count,
                selected: pendingMonths.includes(key),
                disabled: count === 0
            }
        })
    }, [monthFacets, currentMonth, locale, pendingMonths])

    const cityLabels = useMemo(() => {
        const byKey = new Map(cityFacets.map(facet => [facet.key, facet.label]))
        // A key with no facet is a stale link; it is shown as typed rather than dropped silently.
        return pendingCities.map(key => byKey.get(key) ?? key)
    }, [cityFacets, pendingCities])

    const handleApply = useCallback(
        () => onApply(pendingCities, pendingMonths),
        [onApply, pendingCities, pendingMonths]
    )

    const isDirty = !sameValues(pendingCities, filters.cities) || !sameValues(pendingMonths, filters.months)

    return (
        <PackagesSearchBar
            destinationLabel={t('searchBarDestinationLabel')}
            monthLabel={t('searchBarDatesLabel')}
            destinationPlaceholder={t('searchBarDestination')}
            destinationValueLabel={cityLabels.length ? cityLabels.join(', ') : undefined}
            anywhereLabel={t('searchBarAnywhere')}
            anywhereHint={t('searchBarBrowseAll', { count: totalPackages })}
            popularLabel={t('searchBarPopularDestinations')}
            cityOptions={popularCities}
            onToggleCity={key => setPendingCities(current => toggleFilterValue(current, key))}
            // "Browse all N packages" means the catalogue, so it opens the listing unfiltered
            // rather than clearing one field and leaving the reader where they were.
            onAnywhere={() => router.push(withHolidayAppMode(HOLIDAY_PACKAGES_PATH, isApp))}
            monthPlaceholder={t('searchBarAddDates')}
            monthValueLabel={
                pendingMonths.length
                    ? pendingMonths.map(month => formatHolidayMonth(month, locale)).join(', ')
                    : undefined
            }
            monthTitle={t('searchBarSelectMonths')}
            monthHint={t('searchBarMonthHint')}
            monthTiles={monthTiles}
            onToggleMonth={key => setPendingMonths(current => toggleFilterValue(current, key))}
            onClearMonths={() => setPendingMonths([])}
            doneLabel={t('searchBarDone')}
            clearLabel={t('regionClear')}
            packageCountLabel={count => t('regionPackageCount', { count })}
            applyLabel={applyLabel ?? t('searchBarUpdateResults')}
            onApply={handleApply}
            // Nothing to update until the draft differs from what the grid already shows.
            applyDisabled={applyMode === 'filter' && !isDirty}
            isMobile={isMobile}
        />
    )
}

export { PackagesSearchBarWrapper }
export default PackagesSearchBarWrapper
